import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Search, Calendar, Clock, Pencil, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { getPendingBoardMessages, updateBoardMessage, deleteBoardMessage } from '@/lib/api';

export function PendingMessagesDialog({ open, onOpenChange, onRefresh }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editDueDate, setEditDueDate] = useState('');

  const fetchPending = async () => {
    setLoading(true);
    try { setMessages(await getPendingBoardMessages()); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) fetchPending(); }, [open]);

  const fmtDate = (d) => {
    if (!d) return '';
    try { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return d; }
  };

  const handleEdit = (msg) => {
    setEditingId(msg.id);
    setEditContent(msg.content);
    setEditDueDate(msg.due_date || '');
  };

  const handleSave = async (id) => {
    try {
      await updateBoardMessage(id, { content: editContent, due_date: editDueDate || null });
      toast.success('Updated');
      setEditingId(null);
      fetchPending();
      onRefresh?.();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pending message?')) return;
    try {
      await deleteBoardMessage(id);
      toast.success('Deleted');
      fetchPending();
      onRefresh?.();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="pending-messages-dialog">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-semibold">Pending Messages</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-2">
          {loading ? (
            <p className="text-center py-6 text-[13px] text-gray-400">Loading...</p>
          ) : messages.length === 0 ? (
            <p className="text-center py-6 text-[13px] text-gray-400">No pending messages</p>
          ) : messages.map(msg => (
            <div key={msg.id} className="px-3 py-2.5 rounded-lg border hover:bg-gray-50" data-testid={`pending-msg-${msg.id}`}>
              {editingId === msg.id ? (
                <div className="space-y-2">
                  <Textarea value={editContent} onChange={e => setEditContent(e.target.value)} className="min-h-[60px] text-[13px]" />
                  <div className="flex gap-2 items-center">
                    <Label className="text-[12px] text-gray-500">Due:</Label>
                    <Input type="date" className="h-8 text-[12px] w-40" value={editDueDate} onChange={e => setEditDueDate(e.target.value)} />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="h-7 text-[11px] bg-teal-600 hover:bg-teal-700" onClick={() => handleSave(msg.id)}>
                      <Save className="h-3 w-3 mr-1" /> Save
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setEditingId(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-[13px] text-gray-700">{msg.content}</p>
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="text-[11px] text-gray-500">To: {msg.assigned_to_name}</span>
                    <span className="text-[11px] text-amber-600 flex items-center gap-0.5">
                      <Clock className="h-3 w-3" /> Posts {fmtDate(msg.post_date)}
                    </span>
                    {msg.due_date && (
                      <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                        <Calendar className="h-3 w-3" /> Due {fmtDate(msg.due_date)}
                      </span>
                    )}
                    <div className="ml-auto flex gap-1">
                      <button onClick={() => handleEdit(msg)} className="p-1 text-gray-400 hover:text-teal-600" data-testid={`edit-pending-${msg.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => handleDelete(msg.id)} className="p-1 text-gray-400 hover:text-red-500" data-testid={`delete-pending-${msg.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
